import type { ReactNode } from 'react';
import { FloatingMenu } from '../../../FloatingMenu';
import type { DmMessageFrustration } from '../../../../services/dmMessagesInbox';
import type { PickerMenuAlign } from '../../../shared/picker/PickerMenuShell';
import { PickerTriggerButton } from '../../../shared/picker/PickerTriggerButton';
import { usePickerMenu } from '../../../shared/picker/usePickerMenu';
import {
    FRUSTRATION_META,
    fromFrustrationOption,
    toFrustrationOption,
    type FrustrationOption
} from './frustration';
import { FrustrationGlyph } from './FrustrationGlyph';
import { FrustrationMenu } from './FrustrationMenu';

interface FrustrationPickerProps {
    value: DmMessageFrustration;
    onSelect: (value: DmMessageFrustration) => void;
    align?: PickerMenuAlign;
    dropUp?: boolean;
    ariaLabel?: string;
    tooltip?: ReactNode;
    compact?: boolean;
    allowUnset?: boolean;
}

// The priority picker's button and menu, so the two controls line up and read alike.
export function FrustrationPicker({
    value,
    onSelect,
    align = 'left',
    dropUp = false,
    ariaLabel,
    tooltip,
    compact,
    allowUnset
}: FrustrationPickerProps) {
    const menu = usePickerMenu();
    const current = toFrustrationOption(value);
    const meta = FRUSTRATION_META[current];

    const handleSelect = (option: FrustrationOption) => {
        menu.closeToTrigger();
        onSelect(fromFrustrationOption(option));
    };

    return (
        <div
            ref={menu.rootRef}
            onKeyDown={menu.onKeyDown}
            // min-w-0 lets a compact trigger shrink inside the AI marker's flex wrapper.
            className={compact ? 'min-w-0' : undefined}
        >
            <PickerTriggerButton
                glyph={<FrustrationGlyph lit={meta.litBars} className={meta.buttonGlyphTone} />}
                label={meta.label}
                toneClassName={meta.buttonTone}
                chevronClassName={meta.chevronTone}
                bold={meta.bold}
                ariaLabel={ariaLabel}
                tooltip={tooltip}
                compact={compact}
                onClick={(e) => {
                    e.stopPropagation();
                    menu.toggle();
                }}
            />
            <FloatingMenu
                open={menu.open}
                triggerRef={menu.rootRef}
                onClose={menu.close}
                align={align}
                preferUp={dropUp}
            >
                <FrustrationMenu
                    current={current}
                    onSelect={handleSelect}
                    allowUnset={allowUnset}
                />
            </FloatingMenu>
        </div>
    );
}
